import { type FC, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { MultiSelect, type Option } from 'react-multi-select-component';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { TemporaryAccessCodeStatus } from '../../../modules/enums/status';
import {
  AccessCodeDurationUnit,
  AccessCodeKind,
  ReservationState,
} from '../../../modules/enums/types';
import { useNotifications } from '../../../modules/hooks';
import type { CreateAccessCodePayload, LockRecord } from '../../../modules/interfaces';
import { useLocks } from '../../Dashboards/LockManagement/useLocks';
import { useAccessCodes } from '../../Dashboards/TemporaryAccessCodes/useAccessCodes';
import { ReservationPickerTable } from './ReservationPickerTable';
import type { ReservationOption } from './useReservationOptions';

interface AccessCodeModalProps {
  show: boolean;
  handleClose: () => void;
  onSaved?: () => void;
}

const DURATION_UNIT_OPTIONS: { value: AccessCodeDurationUnit; label: string }[] = [
  { value: AccessCodeDurationUnit.MINUTES, label: 'Minutes' },
  { value: AccessCodeDurationUnit.HOURS, label: 'Hours' },
  { value: AccessCodeDurationUnit.DAYS, label: 'Days' },
  { value: AccessCodeDurationUnit.WEEKS, label: 'Weeks' },
];

// A reservation code can be created for a Confirmed or Started reservation.
const CREATE_RESERVATION_STATES = [ReservationState.CONFIRMED, ReservationState.STARTED];

/** Staff-facing create modal — creates a `temporary` code, or a `reservation` code tied to a
 * picked reservation. */
export const AccessCodeModal: FC<AccessCodeModalProps> = ({
  show,
  handleClose,
  onSaved,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const accessCodes = useAccessCodes();
  const locksService = useLocks();

  // The provider is the property's configured door-access provider, sent under the hood.
  const provider = selectedProperty?.property?.doorAccessProvider;

  const [name, setName] = useState('');
  const [selectedLocks, setSelectedLocks] = useState<Option[]>([]);
  const [locks, setLocks] = useState<LockRecord[]>([]);
  const [duration, setDuration] = useState('1');
  const [durationUnit, setDurationUnit] = useState<AccessCodeDurationUnit>(
    AccessCodeDurationUnit.DAYS,
  );
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);

  const [kind, setKind] = useState<AccessCodeKind>(AccessCodeKind.TEMPORARY);
  const [selectedReservation, setSelectedReservation] =
    useState<ReservationOption | null>(null);
  // Reservations that already have an ENABLED reservation code — excluded from the picker so
  // a disabled code can still be recreated.
  const [linkedReservationIds, setLinkedReservationIds] = useState<Set<string>>(
    new Set(),
  );
  const isReservation = kind === AccessCodeKind.RESERVATION;

  const lockOptions = useMemo<Option[]>(
    () => locks.map((lock) => ({ label: lock.name, value: lock.uuid })),
    [locks],
  );

  useEffect(() => {
    if (!show) return;
    setKind(AccessCodeKind.TEMPORARY);
    setName('');
    setSelectedLocks([]);
    setDuration('1');
    setDurationUnit(AccessCodeDurationUnit.DAYS);
    setGuestFirstName('');
    setGuestLastName('');
    setGuestEmail('');
    setPhoneNumber('');
    setSelectedReservation(null);
    locksService
      .list()
      .then(setLocks)
      .catch(() =>
        setSimpleToasts({ type: 'danger', message: 'Failed to load locks', show: true }),
      );
    // Which reservations already have an enabled reservation code — to exclude them.
    accessCodes
      .list({
        kind: AccessCodeKind.RESERVATION,
        status: TemporaryAccessCodeStatus.ENABLED,
      })
      .then((codes) =>
        setLinkedReservationIds(
          new Set(
            codes.flatMap((code) =>
              code.reservation?.uuid ? [code.reservation.uuid] : [],
            ),
          ),
        ),
      )
      .catch(() => setLinkedReservationIds(new Set()));
  }, [show, locksService, accessCodes, setSimpleToasts]);

  const handleSave = async () => {
    if (!provider) {
      setSimpleToasts({
        type: 'danger',
        message: 'This property has no door access provider configured',
        show: true,
      });
      return;
    }

    const lockUuids = selectedLocks.map((option) => option.value);
    let payload: CreateAccessCodePayload;

    if (isReservation) {
      if (!selectedReservation) {
        setSimpleToasts({
          type: 'danger',
          message: 'Please select a reservation',
          show: true,
        });
        return;
      }
      payload = {
        name: name.trim() || undefined,
        kind: AccessCodeKind.RESERVATION,
        provider,
        lockUuids,
        reservationUuid: selectedReservation.reservationId,
        startDate: selectedReservation.startDate,
        endDate: selectedReservation.endDate,
      };
    } else {
      // Temporary codes still require at least one lock; reservation codes don't (the
      // reservation supplies the locks).
      if (selectedLocks.length === 0) {
        setSimpleToasts({
          type: 'danger',
          message: 'Please select at least one lock',
          show: true,
        });
        return;
      }
      const durationValue = Number(duration);
      if (!durationValue || durationValue <= 0) {
        setSimpleToasts({
          type: 'danger',
          message: 'Please enter a valid duration',
          show: true,
        });
        return;
      }
      payload = {
        name: name.trim() || undefined,
        kind: AccessCodeKind.TEMPORARY,
        provider,
        lockUuids,
        duration: durationValue,
        durationUnit,
        guestFirstName: guestFirstName.trim() || undefined,
        guestLastName: guestLastName.trim() || undefined,
        guestEmail: guestEmail.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
      };
    }

    setSaving(true);
    try {
      await accessCodes.create(payload);
      setSimpleToasts({ type: 'success', message: 'Access code created', show: true });
      onSaved?.();
      handleClose();
    } catch {
      setSimpleToasts({
        type: 'danger',
        message: 'Failed to create access code',
        show: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>New access code</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <Form.Select
            value={kind}
            onChange={(event) => setKind(event.target.value as AccessCodeKind)}
          >
            <option value={AccessCodeKind.TEMPORARY}>Temporary</option>
            <option value={AccessCodeKind.RESERVATION}>Reservation</option>
          </Form.Select>
        </Form.Group>

        {isReservation && (
          <Form.Group className="mb-3">
            <Form.Label>Reservation</Form.Label>
            <ReservationPickerTable
              active={show && isReservation}
              states={CREATE_RESERVATION_STATES}
              excludeReservationIds={linkedReservationIds}
              emptyLabel="No reservations available (all already have a code)."
              onSelect={setSelectedReservation}
            />
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Label (optional)</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. Front desk staff"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Provider</Form.Label>
          {/* Read-only for now: inherited from the selected property's
              door-access provider (per-property providers come later). */}
          <Form.Select value={provider ?? ''} disabled>
            {provider ? (
              <option value={provider}>{provider}</option>
            ) : (
              <option value="">No provider configured</option>
            )}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{isReservation ? 'Locks (optional)' : 'Locks'}</Form.Label>
          <MultiSelect
            options={lockOptions}
            value={selectedLocks}
            onChange={setSelectedLocks}
            labelledBy="Select locks"
          />
        </Form.Group>

        {!isReservation && (
          <>
            <Row>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={duration}
                    onChange={(event) => setDuration(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit</Form.Label>
                  <Form.Select
                    value={durationUnit}
                    onChange={(event) =>
                      setDurationUnit(event.target.value as AccessCodeDurationUnit)
                    }
                  >
                    {DURATION_UNIT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Guest first name (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={guestFirstName}
                    onChange={(event) => setGuestFirstName(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Guest last name (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={guestLastName}
                    onChange={(event) => setGuestLastName(event.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Guest email (optional)</Form.Label>
                  <Form.Control
                    type="email"
                    value={guestEmail}
                    onChange={(event) => setGuestEmail(event.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone number (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="cancel-button" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          className="save-button"
          onClick={handleSave}
          disabled={saving || (isReservation && !selectedReservation)}
        >
          {saving ? 'Creating…' : 'Create code'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
